import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import { useAssetApi } from "../api/index.ts";
import type { AssetQueryCacheData } from "./assetQuery.ts";
import { getAssetUrlQueryKey } from "./assetQueryKeys.ts";

export const useAssetUploader = () => {
  const queryClient = useQueryClient();
  const assetApi = useAssetApi();

  const abortController = useRef<AbortController>(undefined);

  useEffect(() => {
    // abort upload on unmount. otherwise the callback will fire too late e.g. to update config
    return () => {
      if (abortController.current) {
        abortController.current.abort();
        abortController.current = undefined;
      }
    };
  }, []);

  return useMemo(
    () => ({
      receiveFile: async (file: File) => {
        abortController.current = new AbortController();
        const signal = abortController.current.signal;
        const { assetId, uploadTo, request } = await assetApi.requestUpload(file, { signal, contentType: file.type });
        try {
          // Update the query cache immediately so processing (e.g. poster image) can happen while uploading
          const cacheData: AssetQueryCacheData = {
            assetId,
            // a File is a Blob (with a name).
            blob: file,
          };
          queryClient.setQueryData(getAssetUrlQueryKey(assetId), cacheData);

          // TODO: this is the rub where we need to palm the upload part off elsewhere in system UI
          // we have asset ID at this point so can continue but the downloadUrl will need to be stubbed while the upload is in progress
          if (uploadTo) {
            // if already there you won't get an uploadTo
            await assetApi.uploadAsset(file, { signal, request, assetId, uploadTo, cancelOnAbort: true });
          }
          return { assetId };
        } catch (err) {
          queryClient.removeQueries({ queryKey: getAssetUrlQueryKey(assetId) });
          throw err;
        } finally {
          abortController.current = undefined;
        }
      },
      cancelUpload: () => {
        if (!abortController.current) return;
        abortController.current.abort();
        abortController.current = undefined;
      },
    }),
    [queryClient, assetApi],
  );
};
